import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { 
  Building, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  EyeOff,
  ArrowLeft,
  Save,
  X,
  Calendar,
  Users,
  Award,
  Target,
  History,
  Upload,
  Image as ImageIcon,
  XCircle,
  Lightbulb
} from 'lucide-react'

const AdminCompany = () => {
  const [contents, setContents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditor, setShowEditor] = useState(false)
  const [editingContent, setEditingContent] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    section_type: 'introduction',
    title: '',
    content: '',
    display_order: 0,
    is_active: true
  })
  const [uploadingImage, setUploadingImage] = useState(false)
  const [currentImageUrl, setCurrentImageUrl] = useState('')
  const [imagePreview, setImagePreview] = useState('')
  // 科技创新专属字段
  const [innovationDesc, setInnovationDesc] = useState('')
  const [innovationFeatures, setInnovationFeatures] = useState([{ title: '', description: '' }])
  // 荣誉资质专属字段（每项含 title、year、description、category、image）
  const [honorsItems, setHonorsItems] = useState([])
  const [honorUploadingIndex, setHonorUploadingIndex] = useState(-1)
  // 核心团队专属字段（每项含 name、position、description、image）
  const [teamMembers, setTeamMembers] = useState([])
  const [teamUploadingIndex, setTeamUploadingIndex] = useState(-1)
  // 发展历程专属字段（每项含 year、event、description）
  const [historyTimeline, setHistoryTimeline] = useState([])
  // 企业文化专属字段（mission/vision/philosophy/rd_philosophy 为文本，core_values 为卡片数组）
  const [valuesMission, setValuesMission] = useState('')
  const [valuesVision, setValuesVision] = useState('')
  const [valuesPhilosophy, setValuesPhilosophy] = useState('')
  const [valuesRdPhilosophy, setValuesRdPhilosophy] = useState('')
  const [valuesCoreValues, setValuesCoreValues] = useState([])
  const navigate = useNavigate()

  // 内容类型配置
  const sectionTypes = [
    { value: 'introduction', label: '公司介绍', icon: Building, color: 'bg-blue-100 text-blue-600' },
    { value: 'history', label: '发展历程', icon: History, color: 'bg-green-100 text-green-600' },
    { value: 'team', label: '团队介绍', icon: Users, color: 'bg-purple-100 text-purple-600' },
    { value: 'honors', label: '荣誉资质', icon: Award, color: 'bg-yellow-100 text-yellow-600' },
    { value: 'values', label: '企业文化', icon: Target, color: 'bg-red-100 text-red-600' },
    { value: 'innovation', label: '科技创新', icon: Lightbulb, color: 'bg-indigo-100 text-indigo-600' }
  ]

  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (!token || !userData) {
      navigate('/admin/login')
      return
    }

    loadContents()
  }, [navigate])

  const loadContents = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/company-contents', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setContents(data.data)
        }
      }
    } catch (error) {
      console.error('加载公司介绍内容失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSectionTypeLabel = (type) => {
    const section = sectionTypes.find(s => s.value === type)
    return section ? section.label : type
  }

  const getSectionTypeIcon = (type) => {
    const section = sectionTypes.find(s => s.value === type)
    return section ? section.icon : Building
  }

  const getSectionTypeColor = (type) => {
    const section = sectionTypes.find(s => s.value === type)
    return section ? section.color : 'bg-gray-100 text-gray-600'
  }

  const handleToggleStatus = async (contentId, currentStatus) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/company-contents/${contentId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setContents(contents.map(content => 
            content.id === contentId 
              ? { ...content, is_active: !currentStatus }
              : content
          ))
        }
      }
    } catch (error) {
      console.error('切换内容状态失败:', error)
    }
  }

  const handleDelete = async (contentId) => {
    if (!window.confirm('确定要删除这个内容吗？')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/company-contents/${contentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setContents(contents.filter(c => c.id !== contentId))
        }
      }
    } catch (error) {
      console.error('删除内容失败:', error)
    }
  }

  const handleCreate = () => {
    setFormData({
      section_type: 'introduction',
      title: '',
      content: '',
      display_order: contents.length,
      is_active: true
    })
    setCurrentImageUrl('')
    setImagePreview('')
    setInnovationDesc('')
    setInnovationFeatures([{ title: '', description: '' }])
    setHonorsItems([])
    setTeamMembers([])
    setHistoryTimeline([])
    setValuesMission('')
    setValuesVision('')
    setValuesPhilosophy('')
    setValuesRdPhilosophy('')
    setValuesCoreValues([])
    setEditingContent(null)
    setIsEditing(false)
    setShowEditor(true)
  }

  const handleEdit = (content) => {
    let contentText = ''
    
    // 科技创新类型：解析 description 和 features
    if (content.section_type === 'innovation' && content.content && typeof content.content === 'object') {
      setInnovationDesc(content.content.description || '')
      setInnovationFeatures(
        content.content.features && content.content.features.length > 0
          ? content.content.features.map(f => ({ title: f.title || '', description: f.description || '' }))
          : [{ title: '', description: '' }]
      )
      contentText = content.content.description || ''
    } else if (content.section_type === 'honors' && content.content && typeof content.content === 'object') {
      // 荣誉资质类型：解析 items 数组（新格式），或从旧格式迁移
      if (content.content.items && content.content.items.length > 0) {
        // 新格式：直接使用 items 数组
        setHonorsItems(content.content.items.map(item => ({
          title: item.title || '',
          year: item.year || '',
          description: item.description || '',
          category: item.category || 'awards',
          image: item.image || '',
          imagePreview: item.image || '',
          active: item.active !== false
        })))
      } else {
        // 旧格式：从 awards/patents/certifications 数组迁移为 items
        const migratedItems = []
        const awards = content.content.awards || []
        const patents = content.content.patents || []
        const certifications = content.content.certifications || []
        awards.forEach(s => migratedItems.push({ title: s, year: '', description: s, category: 'awards', image: '', imagePreview: '', active: true }))
        patents.forEach(s => migratedItems.push({ title: s, year: '', description: s, category: 'patents', image: '', imagePreview: '', active: true }))
        certifications.forEach(s => migratedItems.push({ title: s, year: '', description: s, category: 'certifications', image: '', imagePreview: '', active: true }))
        setHonorsItems(migratedItems)
      }
      contentText = (content.content.description || content.content.text || '')
    } else if (content.section_type === 'team' && content.content && typeof content.content === 'object') {
      // 核心团队类型：解析 items 数组（新格式），或从旧格式迁移
      if (content.content.items && content.content.items.length > 0) {
        setTeamMembers(content.content.items.map(item => ({
          name: item.name || '',
          position: item.position || '',
          description: item.description || '',
          image: item.image || '',
          imagePreview: item.image || '',
          active: item.active !== false
        })))
      } else if (content.content.members && content.content.members.length > 0) {
        // 旧 members 格式兼容
        setTeamMembers(content.content.members.map(item => ({
          name: item.name || '',
          position: item.position || '',
          description: item.description || '',
          image: item.image || '',
          imagePreview: item.image || '',
          active: item.active !== false
        })))
      } else if (content.content.departments && content.content.departments.length > 0) {
        // 最旧 departments 格式兼容
        setTeamMembers(content.content.departments.map((dept, index) => ({
          name: dept.name || `成员${index + 1}`,
          position: dept.name || '',
          description: dept.description || '',
          image: '',
          imagePreview: '',
          active: true
        })))
      } else {
        setTeamMembers([])
      }
      contentText = (content.content.description || content.content.text || '')
    } else if (content.section_type === 'history' && content.content && typeof content.content === 'object') {
      // 发展历程类型：解析 timeline 数组
      if (content.content.timeline && content.content.timeline.length > 0) {
        setHistoryTimeline(content.content.timeline.map(item => ({
          year: item.year || '',
          event: item.event || '',
          description: item.description || ''
        })))
      } else {
        setHistoryTimeline([])
      }
      contentText = (content.content.text || '')
    } else if (content.section_type === 'values' && content.content && typeof content.content === 'object') {
      // 企业文化类型：解析 mission/vision/core_values/philosophy/rd_philosophy
      setValuesMission(content.content.mission || '')
      setValuesVision(content.content.vision || '')
      setValuesPhilosophy(content.content.philosophy || '')
      setValuesRdPhilosophy(content.content.rd_philosophy || '')
      const rawValues = content.content.values || []
      if (rawValues.length > 0) {
        setValuesCoreValues(rawValues.map((valStr, idx) => {
          if (typeof valStr === 'object' && valStr.title) {
            // 新格式：{icon, title, description}
            return {
              icon: valStr.icon || ICON_OPTIONS[idx % ICON_OPTIONS.length],
              title: valStr.title || '',
              description: valStr.description || ''
            }
          }
          // 旧格式：字符串 "创新驱动：xxx"
          const parts = (typeof valStr === 'string' ? valStr : '').split('：')
          return {
            icon: ICON_OPTIONS[idx % ICON_OPTIONS.length],
            title: parts[0] ? parts[0].trim() : '',
            description: parts.length >= 2 ? parts.slice(1).join('：').trim() : ''
          }
        }))
      } else {
        setValuesCoreValues([])
      }
      contentText = (content.content.mission || '')
    } else if (content.content && typeof content.content === 'object') {
      // 如果是JSON对象，优先使用text字段
      if (content.content.text) {
        contentText = content.content.text
      } else if (typeof content.content === 'string') {
        contentText = content.content
      } else {
        // 其他JSON结构，提取可读文本
        contentText = content.content.description || content.content.mission || ''
      }
    } else {
      contentText = content.content || ''
    }
    
    setFormData({
      section_type: content.section_type,
      title: content.title,
      content: contentText,
      display_order: content.display_order,
      is_active: content.is_active
    })
    
    // 提取图片URL（如果存在）
    let imageUrl = ''
    if (content.content && typeof content.content === 'object') {
      if (content.content.image_url) {
        imageUrl = content.content.image_url
      } else if (content.content.logo_url) {
        imageUrl = content.content.logo_url
      } else if (content.content.cover_url) {
        imageUrl = content.content.cover_url
      }
    }
    
    setCurrentImageUrl(imageUrl)
    setImagePreview(imageUrl)
    setEditingContent(content)
    setIsEditing(true)
    setShowEditor(true)
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleImageUpload = async (file) => {
    if (!file) return null
    
    setUploadingImage(true)
    try {
      const token = localStorage.getItem('token')
      const formData = new FormData()
      formData.append('file', file)
      formData.append('fileType', 'company_content')
      formData.append('configKey', `company.${Date.now()}`)
      
      const response = await fetch('/api/admin/upload/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          return data.data.fileUrl
        }
      }
      return null
    } catch (error) {
      console.error('图片上传失败:', error)
      alert('图片上传失败，请重试')
      return null
    } finally {
      setUploadingImage(false)
    }
  }

  const handleImageSelect = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    
    // 验证文件类型
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      alert('只允许上传图片文件 (JPEG, PNG, GIF, WebP, SVG)')
      return
    }
    
    // 验证文件大小（5MB限制）
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过5MB')
      return
    }
    
    // 显示预览
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target.result)
    }
    reader.readAsDataURL(file)
    
    // 上传图片
    const imageUrl = await handleImageUpload(file)
    if (imageUrl) {
      setCurrentImageUrl(imageUrl)
    }
  }

  const handleRemoveImage = () => {
    setCurrentImageUrl('')
    setImagePreview('')
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')
      const url = isEditing && editingContent 
        ? `/api/admin/company-contents/${editingContent.id}`
        : '/api/admin/company-contents'
      
      const method = isEditing && editingContent ? 'PUT' : 'POST'

      // 根据内容类型构造不同的 content 对象
      let contentObj
      if (formData.section_type === 'innovation') {
        // 科技创新：保存 description + features 数组 + image_url
        contentObj = {
          description: innovationDesc,
          features: innovationFeatures.filter(f => f.title.trim() || f.description.trim())
        }
        if (currentImageUrl) {
          contentObj.image_url = currentImageUrl
        }
      } else if (formData.section_type === 'honors') {
        // 荣誉资质：保存 items 数组（每项含 title/year/description/category/image）
        contentObj = {
          items: honorsItems
            .filter(item => item.title.trim())
            .map(item => ({
              title: item.title.trim(),
              year: item.year.trim(),
              description: item.description.trim(),
              category: item.category || 'awards',
              image: item.image || '',
              active: item.active !== false
            }))
        }
      } else if (formData.section_type === 'team') {
        // 核心团队：保存 items 数组（每项含 name/position/description/image/active）
        contentObj = {
          items: teamMembers
            .filter(member => member.name.trim())
            .map(member => ({
              name: member.name.trim(),
              position: member.position.trim(),
              description: member.description.trim(),
              image: member.image || '',
              active: member.active !== false
            }))
        }
      } else if (formData.section_type === 'history') {
        // 发展历程：保存 timeline 数组（每项含 year/event/description）
        contentObj = {
          timeline: historyTimeline
            .filter(item => item.year.trim() || item.event.trim())
            .map(item => ({
              year: item.year.trim(),
              event: item.event.trim(),
              description: item.description.trim()
            }))
        }
      } else if (formData.section_type === 'values') {
        // 企业文化：保存 mission/vision/values(核心价值观)/philosophy/rd_philosophy
        contentObj = {
          mission: valuesMission.trim(),
          vision: valuesVision.trim(),
          values: valuesCoreValues
            .filter(item => item.title.trim())
            .map(item => ({
              icon: item.icon || '⭐',
              title: item.title.trim(),
              description: item.description.trim()
            })),
          philosophy: valuesPhilosophy.trim(),
          rd_philosophy: valuesRdPhilosophy.trim()
        }
      } else {
        // 其他类型：保存纯文本 + image_url
        contentObj = {
          text: formData.content
        }
        if (currentImageUrl) {
          contentObj.image_url = currentImageUrl
        }
      }

      const requestData = {
        ...formData,
        content: contentObj
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          if (isEditing && editingContent) {
            // 更新现有内容
            setContents(contents.map(c => 
              c.id === editingContent.id ? data.data : c
            ))
          } else {
            // 添加新内容
            setContents([...contents, data.data])
          }
          setShowEditor(false)
          setEditingContent(null)
          setIsEditing(false)
          setFormData({
            section_type: 'introduction',
            title: '',
            content: '',
            display_order: 0,
            is_active: true
          })
          setCurrentImageUrl('')
          setImagePreview('')
          setInnovationDesc('')
          setInnovationFeatures([{ title: '', description: '' }])
          setHonorsItems([])
          setTeamMembers([])
          setHistoryTimeline([])
          setValuesMission('')
          setValuesVision('')
          setValuesPhilosophy('')
          setValuesRdPhilosophy('')
          setValuesCoreValues([])
        }
      }
    } catch (error) {
      console.error('保存内容失败:', error)
      alert('保存失败，请重试')
    }
  }

  const handleCancel = () => {
    setShowEditor(false)
    setEditingContent(null)
    setIsEditing(false)
    setFormData({
      section_type: 'introduction',
      title: '',
      content: '',
      display_order: 0,
      is_active: true
    })
    setCurrentImageUrl('')
    setImagePreview('')
    setInnovationDesc('')
    setInnovationFeatures([{ title: '', description: '' }])
    setHonorsItems([])
    setTeamMembers([])
    setHistoryTimeline([])
    setValuesMission('')
    setValuesVision('')
    setValuesPhilosophy('')
    setValuesRdPhilosophy('')
    setValuesCoreValues([])
  }

  // 科技创新 features 管理
  const handleAddFeature = () => {
    setInnovationFeatures(prev => [...prev, { title: '', description: '' }])
  }

  const handleRemoveFeature = (index) => {
    setInnovationFeatures(prev => prev.filter((_, i) => i !== index))
  }

  const handleFeatureChange = (index, field, value) => {
    setInnovationFeatures(prev => prev.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    ))
  }

  // 荣誉资质 items 管理
  const handleAddHonorItem = () => {
    setHonorsItems(prev => [...prev, { title: '', year: '', description: '', category: 'awards', image: '', imagePreview: '', active: true }])
  }

  const handleRemoveHonorItem = (index) => {
    setHonorsItems(prev => prev.filter((_, i) => i !== index))
  }

  const handleToggleHonorItemActive = (index) => {
    setHonorsItems(prev => prev.map((item, i) =>
      i === index ? { ...item, active: !item.active } : item
    ))
  }

  const handleHonorItemChange = (index, field, value) => {
    setHonorsItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ))
  }

  const handleHonorImageUpload = async (index, file) => {
    if (!file) return
    try {
      setHonorUploadingIndex(index)
      const token = localStorage.getItem('token')
      const fd = new FormData()
      fd.append('file', file)
      fd.append('fileType', 'company_content')
      fd.append('configKey', `honors.item.${Date.now()}`)
      const response = await fetch('/api/admin/upload/settings', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          const url = data.data.fileUrl
          setHonorsItems(prev => prev.map((item, i) =>
            i === index ? { ...item, image: url, imagePreview: url } : item
          ))
        }
      }
    } catch (error) {
      console.error('荣誉图片上传失败:', error)
      alert('图片上传失败，请重试')
    } finally {
      setHonorUploadingIndex(-1)
    }
  }

  const handleHonorImageSelect = (index, e) => {
    const file = e.target.files[0]
    if (!file) return
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      alert('只允许上传图片文件 (JPEG, PNG, GIF, WebP, SVG)')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过5MB')
      return
    }
    // 本地预览
    const reader = new FileReader()
    reader.onload = (ev) => {
      setHonorsItems(prev => prev.map((item, i) =>
        i === index ? { ...item, imagePreview: ev.target.result } : item
      ))
    }
    reader.readAsDataURL(file)
    handleHonorImageUpload(index, file)
  }

  const handleRemoveHonorImage = (index) => {
    setHonorsItems(prev => prev.map((item, i) =>
      i === index ? { ...item, image: '', imagePreview: '' } : item
    ))
  }

  // 核心团队 members 管理
  const handleAddTeamMember = () => {
    setTeamMembers(prev => [...prev, { name: '', position: '', description: '', image: '', imagePreview: '', active: true }])
  }

  const handleRemoveTeamMember = (index) => {
    setTeamMembers(prev => prev.filter((_, i) => i !== index))
  }

  const handleToggleTeamMemberActive = (index) => {
    setTeamMembers(prev => prev.map((item, i) =>
      i === index ? { ...item, active: !item.active } : item
    ))
  }

  const handleTeamMemberChange = (index, field, value) => {
    setTeamMembers(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ))
  }

  const handleTeamImageUpload = async (index, file) => {
    if (!file) return
    try {
      setTeamUploadingIndex(index)
      const token = localStorage.getItem('token')
      const fd = new FormData()
      fd.append('file', file)
      fd.append('fileType', 'company_content')
      fd.append('configKey', `team.member.${Date.now()}`)
      const response = await fetch('/api/admin/upload/settings', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          const url = data.data.fileUrl
          setTeamMembers(prev => prev.map((item, i) =>
            i === index ? { ...item, image: url, imagePreview: url } : item
          ))
        }
      }
    } catch (error) {
      console.error('团队成员图片上传失败:', error)
      alert('图片上传失败，请重试')
    } finally {
      setTeamUploadingIndex(-1)
    }
  }

  const handleTeamImageSelect = (index, e) => {
    const file = e.target.files[0]
    if (!file) return
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      alert('只允许上传图片文件 (JPEG, PNG, GIF, WebP, SVG)')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过5MB')
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => {
      setTeamMembers(prev => prev.map((item, i) =>
        i === index ? { ...item, imagePreview: ev.target.result } : item
      ))
    }
    reader.readAsDataURL(file)
    handleTeamImageUpload(index, file)
  }

  const handleRemoveTeamImage = (index) => {
    setTeamMembers(prev => prev.map((item, i) =>
      i === index ? { ...item, image: '', imagePreview: '' } : item
    ))
  }

  // 发展历程 timeline 管理
  const handleAddTimelineItem = () => {
    setHistoryTimeline(prev => [...prev, { year: '', event: '', description: '' }])
  }

  const handleRemoveTimelineItem = (index) => {
    setHistoryTimeline(prev => prev.filter((_, i) => i !== index))
  }

  const handleTimelineItemChange = (index, field, value) => {
    setHistoryTimeline(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ))
  }

  // 企业文化 core_values 卡片管理
  const ICON_OPTIONS = ['🚀', '🤝', '🎯', '💎', '⭐', '💡', '🔬', '🏆', '🌟', '❤️']

  const handleAddCoreValue = () => {
    setValuesCoreValues(prev => [...prev, { icon: '⭐', title: '', description: '' }])
  }

  const handleRemoveCoreValue = (index) => {
    setValuesCoreValues(prev => prev.filter((_, i) => i !== index))
  }

  const handleCoreValueChange = (index, field, value) => {
    setValuesCoreValues(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ))
  }

  const handleReorder = async (contentId, direction) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/company-contents/reorder', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contentId,
          direction
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          loadContents() // 重新加载内容以获取新的排序
        }
      }
    } catch (error) {
      console.error('重新排序失败:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <ArrowLeft size={16} />
                <span>返回仪表板</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                公司介绍内容管理
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleCreate}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>添加内容</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 统计信息 */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {sectionTypes.map((section) => {
              const count = contents.filter(c => c.section_type === section.value).length
              const activeCount = contents.filter(c => c.section_type === section.value && c.is_active).length
              const Icon = section.icon
              
              return (
                <div key={section.value} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{section.label}</p>
                      <p className="text-2xl font-bold text-gray-900">{count}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {activeCount} 个活跃 / {count - activeCount} 个隐藏
                      </p>
                    </div>
                    <div className={`p-3 ${section.color} rounded-lg`}>
                      <Icon size={24} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* 内容列表 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                内容列表
              </h2>
            </div>
            <div className="p-6">
              {contents.length > 0 ? (
                <div className="space-y-4">
                  {contents.map((content) => {
                    const Icon = getSectionTypeIcon(content.section_type)
                    const colorClass = getSectionTypeColor(content.section_type)
                    
                    return (
                      <div key={content.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className={`p-2 ${colorClass} rounded-lg`}>
                              <Icon size={20} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="font-medium text-gray-900">
                                  {content.title || '未命名内容'}
                                </h3>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  content.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {content.is_active ? '已启用' : '已禁用'}
                                </span>
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {getSectionTypeLabel(content.section_type)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  排序: {content.display_order}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 mb-3">
                                {typeof content.content === 'string' ? (
                                  <p className="line-clamp-2">{content.content}</p>
                                ) : (
                                  <p className="text-gray-500">[JSON数据]</p>
                                )}
                              </div>
                              <div className="text-xs text-gray-400">
                                创建时间: {new Date(content.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleReorder(content.id, 'up')}
                              className="text-gray-500 hover:text-gray-700 p-1 rounded"
                              disabled={content.display_order === 0}
                            >
                              ↑
                            </button>
                            <button 
                              onClick={() => handleReorder(content.id, 'down')}
                              className="text-gray-500 hover:text-gray-700 p-1 rounded"
                            >
                              ↓
                            </button>
                            <button 
                              onClick={() => handleToggleStatus(content.id, content.is_active)}
                              className={`p-1 rounded ${
                                content.is_active 
                                  ? 'text-yellow-600 hover:text-yellow-800' 
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                            >
                              {content.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            <button 
                              onClick={() => handleEdit(content)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded"
                            >
                              <Edit size={16} />
                            </button>
                            <button 
                              onClick={() => handleDelete(content.id)}
                              className="text-red-600 hover:text-red-800 p-1 rounded"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building className="mx-auto text-gray-400" size={48} />
                  <p className="mt-4 text-gray-500">暂无公司介绍内容</p>
                  <p className="text-sm text-gray-400 mb-6">添加内容以在公司介绍页面展示</p>
                  <button 
                    onClick={handleCreate}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium flex items-center space-x-2 mx-auto"
                  >
                    <Plus size={16} />
                    <span>添加第一个内容</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 操作指南 */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-4">公司介绍内容管理说明</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div className="flex items-start space-x-2">
                <Building className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="font-medium">内容类型</p>
                  <p>支持公司介绍、发展历程、团队介绍、荣誉资质、企业文化、科技创新六种类型</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Eye className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="font-medium">状态管理</p>
                  <p>启用/禁用内容来控制其在公司介绍页面的显示</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Save className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="font-medium">内容格式</p>
                  <p>直接输入纯文本即可，使用空行分隔段落，无需HTML标签，系统自动排版</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <ArrowLeft className="text-blue-600 mt-0.5" size={16} />
                <div>
                  <p className="font-medium">排序功能</p>
                  <p>使用上下箭头调整内容显示顺序，数字越小显示越靠前</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 内容编辑器 */}
      {showEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? '编辑内容' : '添加内容'}
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* 内容类型选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    内容类型
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {sectionTypes.map((section) => {
                      const Icon = section.icon
                      return (
                        <button
                          key={section.value}
                          type="button"
                          onClick={() => {
                            if (formData.section_type === 'innovation' && section.value !== 'innovation') {
                              setInnovationDesc('')
                              setInnovationFeatures([{ title: '', description: '' }])
                            }
                            if (formData.section_type === 'honors' && section.value !== 'honors') {
                              setHonorsItems([])
                            }
                            if (formData.section_type === 'team' && section.value !== 'team') {
                              setTeamMembers([])
                            }
                            if (formData.section_type === 'history' && section.value !== 'history') {
                              setHistoryTimeline([])
                            }
                            if (formData.section_type === 'values' && section.value !== 'values') {
                              setValuesMission('')
                              setValuesVision('')
                              setValuesPhilosophy('')
                              setValuesRdPhilosophy('')
                              setValuesCoreValues([])
                            }
                            setFormData(prev => ({ ...prev, section_type: section.value }))
                          }}
                          className={`p-3 rounded-lg border flex flex-col items-center justify-center space-y-2 ${
                            formData.section_type === section.value
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`p-2 ${section.color} rounded-full`}>
                            <Icon size={20} />
                          </div>
                          <span className="text-xs font-medium">{section.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* 标题 */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    标题
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入内容标题"
                  />
                </div>

                {/* 显示顺序 */}
                <div>
                  <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                    显示顺序
                  </label>
                  <input
                    type="number"
                    id="display_order"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">数字越小，显示越靠前</p>
                </div>

                {/* 内容区域 - 根据类型显示不同编辑器 */}
                {formData.section_type === 'honors' ? (
                  <>
                    {/* 荣誉资质卡片列表 */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          荣誉资质卡片
                        </label>
                        <button
                          type="button"
                          onClick={handleAddHonorItem}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                        >
                          <Plus size={14} />
                          <span>添加卡片</span>
                        </button>
                      </div>
                      <div className="space-y-4">
                        {honorsItems.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">暂无卡片，点击上方按钮添加</p>
                        ) : (
                          honorsItems.map((item, index) => (
                            <div key={index} className={`border rounded-lg p-4 ${item.active === false ? 'bg-gray-100 border-gray-300 opacity-60' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-500">卡片 {index + 1}</span>
                                  {item.active === false && (
                                    <span className="text-xs bg-gray-300 text-gray-600 px-1.5 py-0.5 rounded">已隐藏</span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleHonorItemActive(index)}
                                    className={`p-1 rounded ${item.active === false ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'}`}
                                    title={item.active === false ? '启用此卡片' : '隐藏此卡片'}
                                  >
                                    {item.active === false ? <Eye size={14} /> : <EyeOff size={14} />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveHonorItem(index)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">标题 *</label>
                                  <input
                                    type="text"
                                    placeholder="如：江苏省高新技术企业"
                                    value={item.title}
                                    onChange={(e) => handleHonorItemChange(index, 'title', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">年份</label>
                                  <input
                                    type="text"
                                    placeholder="如：2023"
                                    value={item.year}
                                    onChange={(e) => handleHonorItemChange(index, 'year', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">分类</label>
                                  <select
                                    value={item.category}
                                    onChange={(e) => handleHonorItemChange(index, 'category', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="awards">荣誉资质</option>
                                    <option value="patents">专利成果</option>
                                    <option value="certifications">体系认证</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">描述</label>
                                  <input
                                    type="text"
                                    placeholder="简要描述"
                                    value={item.description}
                                    onChange={(e) => handleHonorItemChange(index, 'description', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                              {/* 卡片图片上传 */}
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">图片</label>
                                {item.imagePreview ? (
                                  <div className="relative inline-block">
                                    <img
                                      src={item.imagePreview}
                                      alt={`卡片 ${index + 1} 预览`}
                                      className="h-24 rounded border object-contain bg-white"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveHonorImage(index)}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer inline-block">
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors w-32 text-center">
                                      {honorUploadingIndex === index ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                                      ) : (
                                        <>
                                          <Upload className="text-gray-400 mx-auto" size={16} />
                                          <span className="text-xs text-gray-500 mt-1 block">上传图片</span>
                                        </>
                                      )}
                                    </div>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                                      onChange={(e) => handleHonorImageSelect(index, e)}
                                      disabled={honorUploadingIndex === index}
                                    />
                                  </label>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        每张卡片代表一项荣誉/专利/认证，可在前台以卡片网格形式展示
                      </p>
                    </div>
                  </>
                ) : formData.section_type === 'team' ? (
                  <>
                    {/* 核心团队成员列表 */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          团队成员
                        </label>
                        <button
                          type="button"
                          onClick={handleAddTeamMember}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                        >
                          <Plus size={14} />
                          <span>添加成员</span>
                        </button>
                      </div>
                      <div className="space-y-4">
                        {teamMembers.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">暂无成员，点击上方按钮添加</p>
                        ) : (
                          teamMembers.map((member, index) => (
                            <div key={index} className={`border rounded-lg p-4 ${member.active === false ? 'bg-gray-100 border-gray-300 opacity-60' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-500">成员 {index + 1}</span>
                                  {member.active === false && (
                                    <span className="text-xs bg-gray-300 text-gray-600 px-1.5 py-0.5 rounded">已隐藏</span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleTeamMemberActive(index)}
                                    className={`p-1 rounded ${member.active === false ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'}`}
                                    title={member.active === false ? '启用此成员' : '隐藏此成员'}
                                  >
                                    {member.active === false ? <Eye size={14} /> : <EyeOff size={14} />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveTeamMember(index)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">姓名 *</label>
                                  <input
                                    type="text"
                                    placeholder="如：张三"
                                    value={member.name}
                                    onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">职位</label>
                                  <input
                                    type="text"
                                    placeholder="如：CEO / 技术总监"
                                    value={member.position}
                                    onChange={(e) => handleTeamMemberChange(index, 'position', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="mb-3">
                                <label className="block text-xs text-gray-500 mb-1">简介</label>
                                <textarea
                                  placeholder="简要介绍该成员"
                                  value={member.description}
                                  onChange={(e) => handleTeamMemberChange(index, 'description', e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-y"
                                />
                              </div>
                              {/* 成员照片上传 */}
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">照片</label>
                                {member.imagePreview ? (
                                  <div className="relative inline-block">
                                    <img
                                      src={member.imagePreview}
                                      alt={`${member.name || '成员'} 照片`}
                                      className="h-24 rounded border object-cover bg-white"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveTeamImage(index)}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer inline-block">
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors w-32 text-center">
                                      {teamUploadingIndex === index ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                                      ) : (
                                        <>
                                          <Upload className="text-gray-400 mx-auto" size={16} />
                                          <span className="text-xs text-gray-500 mt-1 block">上传照片</span>
                                        </>
                                      )}
                                    </div>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                                      onChange={(e) => handleTeamImageSelect(index, e)}
                                      disabled={teamUploadingIndex === index}
                                    />
                                  </label>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        添加团队核心成员信息，包含姓名、职位、简介和照片，前台将以卡片网格展示
                      </p>
                    </div>
                  </>
                ) : formData.section_type === 'innovation' ? (
                  <>
                    {/* 科技创新：描述 */}
                    <div>
                      <label htmlFor="innovationDesc" className="block text-sm font-medium text-gray-700 mb-2">
                        总体描述
                      </label>
                      <textarea
                        id="innovationDesc"
                        value={innovationDesc}
                        onChange={(e) => setInnovationDesc(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                        placeholder="输入科技创新的总体描述文案"
                      />
                    </div>

                    {/* 科技创新：特色列表 */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          创新特色
                        </label>
                        <button
                          type="button"
                          onClick={handleAddFeature}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                        >
                          <Plus size={14} />
                          <span>添加特色</span>
                        </button>
                      </div>
                      <div className="space-y-3">
                        {innovationFeatures.map((feature, index) => (
                          <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="text-gray-400 text-sm font-medium mt-2 min-w-[20px]">
                              {index + 1}.
                            </span>
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                placeholder="特色标题，如：研发投入"
                                value={feature.title}
                                onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                              <textarea
                                placeholder="特色描述"
                                value={feature.description}
                                onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                                rows={2}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-y"
                              />
                            </div>
                            {innovationFeatures.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveFeature(index)}
                                className="text-red-500 hover:text-red-700 p-1 mt-1"
                                title="移除此特色"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        每个特色包含标题和描述，前台将以卡片网格形式展示
                      </p>
                    </div>
                  </>
                ) : formData.section_type === 'history' ? (
                  <>
                    {/* 发展历程时间线列表 */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          时间线节点
                        </label>
                        <button
                          type="button"
                          onClick={handleAddTimelineItem}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                        >
                          <Plus size={14} />
                          <span>添加节点</span>
                        </button>
                      </div>
                      <div className="space-y-4">
                        {historyTimeline.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">暂无时间线节点，点击上方按钮添加</p>
                        ) : (
                          historyTimeline.map((item, index) => (
                            <div key={index} className="border rounded-lg p-4 bg-gray-50 border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-500">节点 {index + 1}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTimelineItem(index)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="删除此节点"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">年份 *</label>
                                  <input
                                    type="text"
                                    placeholder="如：2020"
                                    value={item.year}
                                    onChange={(e) => handleTimelineItemChange(index, 'year', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">事件标题 *</label>
                                  <input
                                    type="text"
                                    placeholder="如：公司成立"
                                    value={item.event}
                                    onChange={(e) => handleTimelineItemChange(index, 'event', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">描述</label>
                                <textarea
                                  placeholder="事件详细描述"
                                  value={item.description}
                                  onChange={(e) => handleTimelineItemChange(index, 'description', e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-y"
                                />
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        每个节点代表发展历程中的一个里程碑，前台将以左右交替时间线形式展示
                      </p>
                    </div>
                  </>
                ) : formData.section_type === 'values' ? (
                  <>
                    {/* 企业文化编辑器 */}
                    <div className="space-y-5">
                      {/* 企业使命 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          企业使命
                        </label>
                        <textarea
                          value={valuesMission}
                          onChange={(e) => setValuesMission(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                          placeholder="如：为客户提供最优质的智能制造解决方案，推动制造业数字化转型"
                        />
                      </div>

                      {/* 企业愿景 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          企业愿景
                        </label>
                        <textarea
                          value={valuesVision}
                          onChange={(e) => setValuesVision(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                          placeholder="如：成为国内领先的工业自动化解决方案提供商"
                        />
                      </div>

                      {/* 核心价值观 - 卡片列表 */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700">
                            核心价值观卡片
                          </label>
                          <button
                            type="button"
                            onClick={handleAddCoreValue}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                          >
                            <Plus size={14} />
                            <span>添加卡片</span>
                          </button>
                        </div>
                        <div className="space-y-3">
                          {valuesCoreValues.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded-lg">
                              暂无关核心价值观卡片，点击上方按钮添加
                            </p>
                          ) : (
                            valuesCoreValues.map((item, index) => (
                              <div key={index} className="border rounded-lg p-4 bg-gray-50 border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-500">卡片 {index + 1}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCoreValue(index)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title="删除此卡片"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                                {/* 图标选择 */}
                                <div className="mb-3">
                                  <label className="block text-xs text-gray-500 mb-1">图标</label>
                                  <div className="flex flex-wrap gap-1.5">
                                    {ICON_OPTIONS.map((icon) => (
                                      <button
                                        key={icon}
                                        type="button"
                                        onClick={() => handleCoreValueChange(index, 'icon', icon)}
                                        className={`w-9 h-9 rounded border text-lg flex items-center justify-center transition-all ${
                                          item.icon === icon
                                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-300'
                                            : 'border-gray-300 bg-white hover:border-gray-400'
                                        }`}
                                        title={icon}
                                      >
                                        {icon}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="block text-xs text-gray-500 mb-1">价值观名称 *</label>
                                  <input
                                    type="text"
                                    placeholder="如：创新驱动"
                                    value={item.title}
                                    onChange={(e) => handleCoreValueChange(index, 'title', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">描述</label>
                                  <textarea
                                    placeholder="价值观描述说明"
                                    value={item.description}
                                    onChange={(e) => handleCoreValueChange(index, 'description', e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-y"
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          前台将以图标卡片网格形式展示，支持 2-4 列自适应布局
                        </p>
                      </div>

                      {/* 经营理念 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          经营理念
                        </label>
                        <textarea
                          value={valuesPhilosophy}
                          onChange={(e) => setValuesPhilosophy(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                          placeholder="如：以质量求生存、以科技增效益，以品牌促发展，以客户为中心"
                        />
                      </div>

                      {/* 研发理念 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          研发理念
                        </label>
                        <textarea
                          value={valuesRdPhilosophy}
                          onChange={(e) => setValuesRdPhilosophy(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                          placeholder="如：科技、创新、创造"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  /* 其他类型：富文本编辑器 */
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      内容
                    </label>
                    <div className="rich-editor-wrapper border border-gray-300 rounded-md overflow-hidden">
                      <ReactQuill
                        value={formData.content}
                        onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                        theme="snow"
                        placeholder="请输入内容，支持段落、加粗、斜体、列表等排版格式..."
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ align: [] }],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            ['blockquote'],
                            ['link'],
                            ['clean']
                          ]
                        }}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'align',
                          'list', 'bullet',
                          'blockquote',
                          'link'
                        ]}
                        className="bg-white"
                        style={{ height: '280px' }}
                      />
                    </div>
                    <p className="mt-14 text-xs text-gray-500">
                      提示：支持标题、加粗、斜体、下划线、删除线、对齐方式、有序/无序列表、引用、超链接等排版功能。内容将自动在前台适配显示。
                    </p>
                  </div>
                )}

                {/* 图片上传 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    图片上传
                  </label>
                  <div className="space-y-4">
                    {/* 图片预览 */}
                    {imagePreview && (
                      <div className="relative">
                        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">图片预览</span>
                            <button
                              type="button"
                              onClick={handleRemoveImage}
                              className="text-red-600 hover:text-red-800"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                          <div className="flex justify-center">
                            <img 
                              src={imagePreview} 
                              alt="预览" 
                              className="max-h-48 rounded-md object-contain"
                            />
                          </div>
                          {currentImageUrl && (
                            <p className="mt-2 text-xs text-gray-500 truncate">
                              图片URL: {currentImageUrl}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 上传按钮 */}
                    <div className="flex items-center justify-center">
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                          <div className="flex flex-col items-center justify-center space-y-2">
                            {uploadingImage ? (
                              <>
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <p className="text-sm text-gray-600">上传中...</p>
                              </>
                            ) : (
                              <>
                                <Upload className="text-gray-400" size={24} />
                                <p className="text-sm font-medium text-gray-700">
                                  {imagePreview ? '更换图片' : '上传图片'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  支持 JPG, PNG, GIF, WebP, SVG 格式，最大 5MB
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                            onChange={handleImageSelect}
                            disabled={uploadingImage}
                          />
                        </div>
                      </label>
                    </div>

                    {/* 图片使用说明 */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <ImageIcon className="text-blue-600 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs font-medium text-blue-800">图片使用说明</p>
                          <p className="text-xs text-blue-700">
                            上传的图片将自动保存到内容中，可以在公司介绍页面展示。
                            图片URL会保存在内容的image_url字段中。
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 状态 */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleFormChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                    启用此内容
                  </label>
                </div>
              </div>
            </div>

            {/* 底部操作按钮 */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isEditing ? '保存更改' : '创建内容'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCompany
